import React, { useEffect, useState, useSyncExternalStore } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  LinearProgress,
  Typography,
} from '@mui/material'
import ResetFlowOverlay from './ResetFlowOverlay'
import FarmSelectionScreen from './FarmSelectionScreen'
import useBeforeUnloadGuard from './useBeforeUnloadGuard'
import {
  endCleanupSession,
  getCleanupSession,
  subscribeCleanupSession,
} from '../../utils/localReset/cleanupSession'
import { isCleanupActive } from '../../utils/localReset/resetMarker'
import { createResumeController } from '../../utils/localReset/resumeCleanup'

const defaultCreateDeps = async () =>
  (await import('../../utils/localReset/defaultDeps')).createResumeDeps()

function ResumeView({ controller, onFinished }) {
  const view = useSyncExternalStore(controller.subscribe, controller.getView)
  useBeforeUnloadGuard(
    view.phase !== 'done' && view.phase !== 'session_invalid',
  )

  useEffect(() => {
    if (view.phase === 'done') onFinished()
  }, [view.phase, onFinished])

  useEffect(() => {
    if (view.phase !== 'waiting_network') return undefined
    const h = () => controller.retry()
    window.addEventListener('online', h)
    return () => window.removeEventListener('online', h)
  }, [view.phase, controller])

  return (
    <Dialog open fullScreen onClose={() => {}} aria-labelledby='resume-title'>
      {view.phase === 'selecting' ? (
        <FarmSelectionScreen
          farms={view.farms}
          selected={view.selected}
          notice={view.notice}
          onToggle={controller.toggle}
          onConfirm={controller.confirm}
        />
      ) : (
        <Box sx={{ maxWidth: 560, mx: 'auto', p: 3, width: '100%' }}>
          <Typography id='resume-title' variant='h5' sx={{ mb: 2 }}>
            Terminando a limpeza dos dados
          </Typography>
          {view.phase === 'session_invalid' ? (
            <>
              <Alert severity='info'>
                Seus dados foram limpos com segurança; entre novamente.
              </Alert>
              <Box sx={{ mt: 2 }}>
                <Button variant='contained' onClick={controller.goToLogin}>
                  Ir para o login
                </Button>
              </Box>
            </>
          ) : (
            <>
              {view.message && (
                <Typography sx={{ mb: 1 }}>{view.message}</Typography>
              )}
              {(view.phase === 'validating' ||
                view.phase === 'pulling_base' ||
                view.phase === 'downloading') && (
                <LinearProgress role='status' />
              )}
              {(view.phase === 'error' || view.phase === 'waiting_network') && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button variant='contained' onClick={controller.retry}>
                    Tentar de novo
                  </Button>
                  {view.canLeave && (
                    <Button onClick={controller.goToLogin}>Sair</Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Dialog>
  )
}

function CleanupResume({ createDeps, onFinished }) {
  const [controller, setController] = useState(null)
  useEffect(() => {
    let alive = true
    createDeps().then((deps) => {
      if (!alive) return
      const c = createResumeController(deps)
      setController(c)
      c.start()
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (!controller) return null
  return <ResumeView controller={controller} onFinished={onFinished} />
}

// Montado em router.js ao redor das rotas. Prioridade: (1) sessão de limpeza em curso -> só o overlay
// (o router é desmontado antes do reset do banco); (2) marcador no boot -> retomada; (3) app normal.
export default function CleanupResumeGate({
  children,
  createResumeDepsFn = defaultCreateDeps,
  isActiveFn = isCleanupActive,
}) {
  const session = useSyncExternalStore(
    subscribeCleanupSession,
    getCleanupSession,
  )
  const [resuming, setResuming] = useState(() => isActiveFn())
  if (session) {
    return (
      <ResetFlowOverlay
        controller={session.controller}
        title='Limpando os dados deste aparelho'
        onClose={endCleanupSession}
      />
    )
  }
  if (resuming) {
    return (
      <CleanupResume
        createDeps={createResumeDepsFn}
        onFinished={() => setResuming(false)}
      />
    )
  }
  return children
}
